'use client';

import { ArrowLeft, ArrowRight } from 'lucide-react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useLocationPreference } from '@/app/(users)/totem-by-slug/[slug]/hooks/use-location-preference';
import { useTotem } from '@/app/(users)/totem-by-slug/[slug]/providers/totem-provider';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export function TotemOrderOptionsOverlay() {
  const {
    orderOptionsOpen,
    setOrderOptionsOpen,
    orderType,
    setOrderType,
    pickupMethod,
    setPickupMethod,
    beeperNumber,
    setBeeperNumber,
    totem,
  } = useTotem();
  const { data: locationPreference } = useLocationPreference(totem?.location_id ?? undefined);

  const pathname = usePathname();
  const isDashboard = pathname?.includes('/takeorder');

  const [step, setStep] = useState<1 | 2>(1);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (pickupMethod !== null) {
      const timeout = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [pickupMethod]);

  // Dynamic step initialization based on hybrid flow
  useEffect(() => {
    if (orderOptionsOpen) {
      if (orderType === 'eat_here') {
        setStep(2);
      } else {
        setStep(1);
      }
    }
  }, [orderOptionsOpen, orderType]);

  if (!orderOptionsOpen) return null;

  const handleTakeaway = () => {
    setOrderType('takeaway');
    setOrderOptionsOpen(false);
  };

  const handleDineIn = () => {
    setOrderType('eat_here');
    setStep(2);
  };

  const handleContinue = () => {
    setOrderOptionsOpen(false);
  };

  const handleBack = () => {
    if (pickupMethod !== null) {
      setPickupMethod(null);
      setBeeperNumber('');
    } else if (step === 2) {
      setStep(1);
    } else {
      setOrderOptionsOpen(false);
    }
  };

  let wrapperScaleClass = '';
  if (pickupMethod !== null) {
    wrapperScaleClass = 'scale-[0.70] origin-top transition-transform duration-300';
  } else {
    wrapperScaleClass = 'scale-[0.75] origin-top transition-transform duration-300';
  }

  return (
    <div
      className={cn(
        'inset-0 z-[100] bg-black/75 backdrop-blur-sm flex flex-col pt-10 px-6 overflow-hidden',
        isDashboard ? 'absolute' : 'fixed'
      )}
    >
      <div className={cn('flex flex-col flex-1 w-full transition-transform', wrapperScaleClass)}>
        {step === 2 && (
          <Button
            variant="ghost"
            className="absolute top-10 left-6 text-white hover:text-white hover:bg-white/10 rounded-full pl-3 pr-5 py-6 font-semibold text-lg"
            onClick={handleBack}
          >
            <div className="bg-white text-black rounded-full p-1.5 mr-3">
              <ArrowLeft className="size-5" />
            </div>
            Volver
          </Button>
        )}

        {/* Header Logo/Title */}
        <div className="flex flex-col items-center gap-2 mb-4 xl:mb-10 text-white mt-0 xl:mt-4">
          {/* Usando el nombre del totem o un titulo generico */}
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-bold">{totem?.locations?.title}</h2>
          </div>
          {step === 2 && (
            <p className="text-xl xl:text-2xl mt-4 xl:mt-8 mb-2 xl:mb-4">
              Seleccioná una opción para retirar tu pedido
            </p>
          )}
        </div>

        <div className="flex-1 flex flex-col items-center justify-center pb-10 xl:pb-20">
          {step === 1 ? (
            <div className="flex flex-col gap-4 sm:gap-6 w-full max-w-lg px-4 sm:px-0">
              {locationPreference?.take_away && (
                <button
                  type="button"
                  onClick={handleTakeaway}
                  className="flex items-center justify-center gap-4 sm:gap-6 lg:gap-8 bg-white hover:bg-slate-50 text-black p-6 sm:p-8 lg:p-10 rounded-3xl transition-transform active:scale-[0.98] shadow-2xl group"
                >
                  <div className="relative w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 shrink-0">
                    <Image src="/icons/take-away.png" alt="Para llevar" fill className="object-contain" />
                  </div>
                  <span className="text-xl sm:text-2xl lg:text-3xl font-bold whitespace-nowrap">Para llevar</span>
                </button>
              )}

              {locationPreference?.eat_here && (
                <button
                  type="button"
                  onClick={handleDineIn}
                  className="flex items-center justify-center gap-4 sm:gap-6 lg:gap-8 bg-white hover:bg-slate-50 text-black p-6 sm:p-8 lg:p-10 rounded-3xl transition-transform active:scale-[0.98] shadow-2xl group"
                >
                  <div className="relative w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 shrink-0">
                    <Image
                      src="/icons/eat-here.png"
                      alt="Para comer aquí"
                      fill
                      className="brightness-0 object-contain"
                    />
                  </div>
                  <span className="text-xl sm:text-2xl lg:text-3xl font-bold whitespace-nowrap">Para comer aquí</span>
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-6 xl:gap-12 w-full max-w-3xl">
              <div className="flex gap-4 xl:gap-6 justify-center w-full">
                {/* TODO: Reemplazar iconos genéricos (Flag, CircleDot, Users) por SVGs personalizados cuando estén disponibles */}
                <button
                  type="button"
                  onClick={() => {
                    if (pickupMethod !== 'banderin') setBeeperNumber('');
                    setPickupMethod('banderin');
                  }}
                  className={cn(
                    'flex-1 flex flex-col items-center gap-3 xl:gap-6 bg-white p-4 xl:p-10 rounded-3xl transition-all border-4 shadow-xl active:scale-[0.98]',
                    pickupMethod === 'banderin' ? 'border-emerald-500 scale-105' : 'border-transparent opacity-90'
                  )}
                >
                  <Image
                    src="/icons/banderin-modal.png"
                    alt="Banderín"
                    width={96}
                    height={96}
                    className="w-16 h-16 xl:w-24 xl:h-24 object-contain"
                  />
                  <span className="text-xl xl:text-2xl font-semibold text-zinc-800">Banderín</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (pickupMethod !== 'beeper') setBeeperNumber('');
                    setPickupMethod('beeper');
                  }}
                  className={cn(
                    'flex-1 flex flex-col items-center gap-3 xl:gap-6 bg-white p-4 xl:p-10 rounded-3xl transition-all border-4 shadow-xl active:scale-[0.98]',
                    pickupMethod === 'beeper' ? 'border-emerald-500 scale-105' : 'border-transparent opacity-90'
                  )}
                >
                  <Image
                    src="/icons/beeper.png"
                    alt="Beeper"
                    width={96}
                    height={96}
                    className="w-16 h-16 xl:w-24 xl:h-24 object-contain"
                  />
                  <span className="text-xl xl:text-2xl font-semibold text-zinc-800">Beeper</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (pickupMethod !== 'nombre') setBeeperNumber('');
                    setPickupMethod('nombre');
                  }}
                  className={cn(
                    'flex-1 flex flex-col items-center gap-3 xl:gap-6 bg-white p-4 xl:p-10 rounded-3xl transition-all border-4 shadow-xl active:scale-[0.98]',
                    pickupMethod === 'nombre' ? 'border-emerald-500 scale-105' : 'border-transparent opacity-90'
                  )}
                >
                  <Image
                    src="/icons/nombre.png"
                    alt="Nombre"
                    width={96}
                    height={96}
                    className="w-16 h-16 xl:w-24 xl:h-24 object-contain"
                  />
                  <span className="text-xl xl:text-2xl font-semibold text-zinc-800">Nombre</span>
                </button>
              </div>

              {/* Identifier Input and Continue Button */}
              {pickupMethod !== null && (
                <div className="bg-white w-full max-w-2xl rounded-3xl p-4 xl:p-8 shadow-2xl animate-in slide-in-from-top-4 fade-in duration-300">
                  <label
                    htmlFor="identifier"
                    className="block text-lg xl:text-2xl font-semibold mb-2 xl:mb-4 text-zinc-800"
                  >
                    {pickupMethod === 'banderin' && 'Ingresá el número de banderín'}
                    {pickupMethod === 'beeper' && 'Ingresá el número de beeper'}
                    {pickupMethod === 'nombre' && 'Ingresá tu nombre'}
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="relative flex-1">
                      {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                      <input
                        ref={inputRef}
                        id="identifier-input"
                        type={pickupMethod === 'nombre' ? 'text' : 'number'}
                        value={beeperNumber}
                        onChange={e => setBeeperNumber(e.target.value)}
                        placeholder={
                          pickupMethod === 'beeper'
                            ? 'Ingresa el número'
                            : pickupMethod === 'banderin'
                              ? 'Ingresa el número'
                              : 'Tu nombre'
                        }
                        className="w-full bg-slate-100 dark:bg-slate-100 rounded-2xl h-14 xl:h-24 text-center text-2xl xl:text-4xl font-bold text-zinc-800 dark:text-zinc-800 outline-none focus:ring-4 focus:ring-emerald-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-400 placeholder:text-lg xl:placeholder:text-2xl placeholder:font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="absolute bottom-4 right-6 text-zinc-400 font-medium text-lg">
                        {beeperNumber.length}/{pickupMethod === 'nombre' ? 20 : 3}
                      </span>
                    </div>

                    <Button
                      size="icon"
                      onClick={handleContinue}
                      disabled={beeperNumber.length === 0}
                      className="shrink-0 h-14 w-14 xl:h-24 xl:w-24 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg disabled:opacity-50 transition-all active:scale-[0.98]"
                    >
                      <ArrowRight className="size-6 xl:size-10 stroke-[3]" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Order Fast Logo */}
        <div className="pb-8 flex justify-center pointer-events-none opacity-80 shrink-0 mt-auto">
          <Image
            src="/icons/of-logo.png"
            alt="Order Fast"
            width={160}
            height={60}
            className="object-contain"
            style={{ width: 'auto', height: 'auto' }}
          />
        </div>
      </div>
    </div>
  );
}
